import { getConfig } from '../config';

const getApiUrl = () => getConfig().API_URL;
let refreshPromise = null;

const getAuthHeaders = () => {
    const user = JSON.parse(localStorage.getItem("user"));

    return user
        ? {
            Authorization: `Bearer ${user.accessToken}`,
        }
        : {};
};

const apiRequest = async (
    endpoint,
    {
        method = "GET",
        body = null,
        headers = {},
        auth = true,
        skipRefresh = false
    } = {}
) => {
    const finalHeaders = {
        Accept: "application/json",
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(auth ? getAuthHeaders() : {}),
        ...headers,
    };

    const httpResponse = await fetch(
        `${getApiUrl()}${endpoint}`,
        {
            method,
            headers: finalHeaders,
            body: body
                ? JSON.stringify(body)
                : null,
        }
    );

    let jsonResponse = null;

    try {
        jsonResponse = await httpResponse.json();
    } catch (e) {
        throw {
            status: httpResponse.status,
            message: "Server returned invalid JSON.",
        };
    }

    if (
        !httpResponse.ok ||
        jsonResponse?.status === "failed"
    ) {
        const user = JSON.parse(localStorage.getItem("user"));
        
        if (httpResponse.status == 401 && 
            !skipRefresh &&
            user?.refreshToken
        ) {
            try {
                if (!refreshPromise) {
                    refreshPromise = refreshAccessToken();
                }

                await refreshPromise;
                refreshPromise = null;

                return apiRequest(
                    endpoint,
                    {
                        method,
                        body,
                        headers,
                        auth,
                        skipRefresh: true,
                    }
                );

            } catch (refreshError) {
                refreshPromise = null;
                localStorage.clear();
                window.location.reload();

                throw {
                    status: 401,
                    message:
                        "Session expired",
                };
            }
        }

        throw {
            status: httpResponse.status,
            message:
                jsonResponse?.message ||
                "Unknown server error",
            data: jsonResponse,
        };
    }

    return jsonResponse;
};

const saveTokens = (accessToken, refreshToken) => {
    const user = JSON.parse(
        localStorage.getItem("user")
    );

    if (!user) {
        return;
    }

    user.accessToken = accessToken;
    user.refreshToken = refreshToken;

    localStorage.setItem(
        "user",
        JSON.stringify(user)
    );
};

const refreshAccessToken = async () => {
    const user = JSON.parse(localStorage.getItem("user"));

    if (!user || !user.refreshToken) {
        throw new Error("No refresh token");
    }

    const response = await apiRequest(
        "/api/refresh",
        {
            method: "POST",
            body: {
                'refresh_token': user.refreshToken
            },
            auth: false,
            skipRefresh: true
        }
    );

    if (!response?.access_token) {
        throw new Error(
            "Refresh token failed"
        );
    }

    saveTokens(
        response.access_token,
        response.refresh_token
    );

    return response.access_token;
};

export const loginUser = (email, password) => {
    return apiRequest("/api/login", {
        method: "POST",
        auth: false,
        body: { email, password }
    });
};

export const registerUser = (
    email,
    password,
    username,
    country
) => {
    return apiRequest("/api/register", {
        method: "POST",
        auth: false,
        body: {
            email,
            password,
            username,
            country,
        },
    });
};

export const forgotPassword = (email) => {
    return apiRequest("/api/password/forgot", {
        method: "POST",
        auth: false,
        body: { email },
    });
};

export const fetchMatches = (tournament = null, fixture = "Next games") => {
    let url = `/api/games`;
    let fixtureSet = false;

    if (fixture !== "Next games") {
        url += `?fixture=${fixture}`;
        fixtureSet = true;
    }
    if (tournament !== null) {
        url += (fixtureSet ? '&' : '?');
        url += `tournament_id=${tournament}`
    }

    return apiRequest(url)
};

export const fetchPredictPageTournaments = () => {
    return apiRequest("/api/tournaments/settings");
}

export const fetchUserProfile = () => {
    return apiRequest("/api/users/profile");
}

export const fetchTodayGames = (
    date = null,
    squadId = null
) => {
    const params = new URLSearchParams();

    if (date) {
        params.append("date", date);
    }

    if (squadId) {
        params.append("squad_id", squadId);
    }

    let endpoint = "/api/games/today";

    if (params.toString()) {
        endpoint += `?${params.toString()}`;
    }

    return apiRequest(endpoint);
};

export const updateProfile = (name, showName, country) => {
    return apiRequest("/api/users/profile", {
        method: "PUT",
        body: {
            'name': name,
            'show_name': showName,
            'country': country
        }
    });
};

export const fetchUserTournaments = async () => {
    return apiRequest("/api/user/tournaments");
};

export const submitPrediction = async (gameId, predictionData) => {
    return apiRequest(`/api/predictions/games/${gameId}`, {
        method: "POST",
        body: {
            'prediction': predictionData
        }
    });
};

export const getLeaderboard = async (season = null, filter = null, sort = null, direction = null) => {
    const params = new URLSearchParams();
    if (season !== null) {
        params.append('season', season);
    }
    if (filter !== null) {
        params.append('filter', filter);
    }
    if (sort !== null) {
        params.append('sort', sort);
    }
    if (direction !== null) {
        params.append('direction', direction);
    }

    const url = `/api/leaderboard${params.toString() ? '?' + params.toString() : ''}`;

    return apiRequest(url)
};

export const fetchUserPoints = async (username = null, filter = null, sort = null, direction = null) => {
    const params = new URLSearchParams();
    if (filter !== null) {
        params.append('filter', filter);
    }
    if (sort !== null) {
        params.append('sort', sort);
    }
    if (direction !== null) {
        params.append('direction', direction);
    }

    const baseUrl = `/api/points` + (username !== null ? `/user/${username}` : '');
    const url = baseUrl + `${params.toString() ? '?' + params.toString() : ''}`;

    return apiRequest(url)
};

export const verifyEmail = async (code) => {
    return apiRequest("/api/verify-email", {
        method: "POST",
        body: {
            code
        }
    });
};

export const resetPassword = async (code, password) => {
    return apiRequest("/api/password/set", {
        method: "POST",
        body: {
            code, password
        }
    });
};

export const fetchUnfinishedGames = async () => {
    return apiRequest("/api/games/pending");
};

export const fetchFinishedGames = async () => {
    return apiRequest("/api/games/finished");
};

export const updateGameResults = async (gameId, home, away, homeHT, awayHT, fix = null) => {
    var results = {
        'home': home,
        'away': away,
        'home-ht': homeHT,
        'away-ht': awayHT
    };
    if (fix !== null) {
        results['fix_wrong_results'] = 1;
    }

    const url = `/api/games/` + gameId

    return apiRequest(url, {
        method: "PUT",
        body: results
    });
};

export const updateGameStartTime = async (gameId, startTime) => {
    const url = `/api/games/` + gameId

    return apiRequest(url, {
        method: "PUT",
        body: {
            'start_time': startTime
        }
    });
};

export const updateGameChances = async (gameId, home, x, away) => {
    var chances = {
        '1': home,
        'X': x,
        '2': away
    };

    const url = `/api/games/` + gameId

    return apiRequest(url, {
        method: "PUT",
        body: {
            "chances": chances
        }
    });
};

export const fetchTournamentsAll = async () => {
    return apiRequest("/api/tournaments/all");
};

export const fetchLinks = async () => {
    return apiRequest("/api/links/all");
};

export const updateTournamentStatus = async (tournamentId, newStatus) => {
    const url = `/api/tournaments/${tournamentId}/abandon`

    return apiRequest(url, {
        method: "PUT"
    });
};

export const updateLinkStatus = async (linkId, newStatus) => {
    const url = `/api/links/${linkId}`

    return apiRequest(url, {
        method: "PUT",
        body: {
            'new_status': newStatus
        }
    });
};

export const toggleUserTournamentEnabled = async (tournamentId) => {
    const url = `/api/user/tournaments/${tournamentId}`

    return apiRequest(url, {
        method: "PUT"
    });
};

export const fetchUserJoinedSquads = async () => {
    return apiRequest("/api/user/squads/joined");
};

export const joinArena = async (arenaId) => {
    const url = `/api/arenas/${arenaId}/join`

    return apiRequest(url, {
        method: "POST"
    });
};

export const findSquadByCode = async (code) => {
    return apiRequest("/api/squads/find", {
        method: "POST",
        body: {
            code
        }
    });
};

export const joinSquad = async (slug, code, arenaIds = []) => {
    return apiRequest(`/api/squads/${slug}/join`, {
        method: "POST",
        body: {
            code: code,
            arena_ids: arenaIds
        }
    });
};

export const createSquad = async (name, country) => {
    return apiRequest(`/api/squads`, {
        method: "POST",
        body: {
            name,
            country
        }
    });
};

export const createArena = async (
    squadSlug,
    tournaments
) => {
    return apiRequest(`/api/squads/${squadSlug}/arenas`, {
        method: "POST",
        body: {
            tournaments
        }
    });
};

export const fetchSeasons = () => {
    return apiRequest("/api/seasons");
};

export const createSeason = (name, status) => {
    return apiRequest("/api/seasons", {
        method: "POST",
        body: {
            name,
            status
        }
    });
};

export const updateSeasonStatus = (id, status) => {
    return apiRequest(`/api/seasons/${id}`, {
        method: "POST",
        body: {
            status
        }
    });
};

export const fetchTournaments = () => {
    return apiRequest("/api/tournaments", {
        auth: false
    });
}
